import React, { Fragment, useCallback, useEffect, useReducer } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { toast } from "react-toastify";
import Grid from "@mui/material/Grid";

import ai from "../helpers/axios";
import PageHeaderRowSearch from "../components/shared/page-header-row-search";
import { FiltersSections, MetricsLocationsFilters } from "../components/metrics-locations/metrics-locations-filters";
import { MetricsLocationsTable, MetricsType } from "../components/metrics-locations/metrics-locations-table";

const tableDefaultState = {
  category: null,
  data: [],
  totalLength: 0,
  countPerPage: 10,
  currentPage: 1,
  sortBy: "revenue",
  sortDirection: "desc",
};

const initialState = {
  isLoading: false,
  filters: {
    [FiltersSections.Dates]: [],
    [FiltersSections.Departure]: {
      category: null,
    },
    [FiltersSections.Arrival]: {
      category: null,
    },
    [FiltersSections.Combo]: {
      category: null,
      values: [],
    },
  },
  [MetricsType.Departure]: tableDefaultState,
  [MetricsType.Arrival]: tableDefaultState,
  [MetricsType.Combo]: tableDefaultState,
};

const reducer = (state, action) => {
  switch (action.type) {
    case "fieldChanged": {
      return {
        ...state,
        [action.field]: action.value,
      };
    }

    case "dataBeganDownloading": {
      return {
        ...state,
        isLoading: true,
      };
    }

    case "dataDownloadFinished": {
      return {
        ...state,
        isLoading: false,
      };
    }

    case "dataDownloaded": {
      const { departure, arrival, combo } = action.data;
      return {
        ...state,
        isLoading: false,
        [MetricsType.Departure]: {
          ...state[MetricsType.Departure],
          category: departure.category,
          data: departure.data,
          totalLength: departure.totalLength,
        },
        [MetricsType.Arrival]: {
          ...state[MetricsType.Arrival],
          category: arrival.category,
          data: arrival.data,
          totalLength: arrival.totalLength,
        },
        [MetricsType.Combo]: {
          ...state[MetricsType.Combo],
          category: combo.category,
          data: combo.data,
          totalLength: combo.totalLength,
        },
      };
    }

    case "filtersChanged": {
      return {
        ...state,
        filters: action.filters,
      };
    }

    case "pageChanged": {
      return {
        ...state,
        [action.payload.type]: {
          ...state[action.payload.type],
          currentPage: action.payload.page,
        },
      };
    }

    case "rowsPerPageChanged": {
      return {
        ...state,
        [action.payload.type]: {
          ...state[action.payload.type],
          countPerPage: action.payload.countPerPage,
          currentPage: 1,
        },
      };
    }

    case "sortingChanged": {
      return {
        ...state,
        [action.payload.type]: {
          ...state[action.payload.type],
          ...([action.payload.type].currentPage !== 1 && {
            currentPage: 1,
          }),
          sortBy: action.payload.sortBy,
          sortDirection: action.payload.sortDirection,
        },
      };
    }

    default:
      break;
  }
};

const MetricsLocations = () => {
  const {
    getAccessTokenSilently,
    loginWithRedirect,
  } = useAuth0();

  useEffect(() => {
    document.title = "Metrics - Top Locations | evoJets HQ";
  }, []);

  const [state, dispatch] = useReducer(reducer, initialState);

  const getLocationsMetrics = useCallback(async ({ filters }, loadSilently = false) => {
    const token = await getAccessTokenSilently().catch(() => {
      return loginWithRedirect();
    });

    if (!filters) {
      return;
    }

    const formatedFilters = {
      dates: filters.dates,
      [MetricsType.Departure]: {
        category: filters[FiltersSections.Departure].category,
        values: filters[FiltersSections.Departure].category ?
          filters[FiltersSections.Departure][filters[FiltersSections.Departure].category]?.map(({ value }) => value) || [] :
          [],
        currentPage: state[MetricsType.Departure].currentPage,
        countPerPage: state[MetricsType.Departure].countPerPage,
        sortBy: state[MetricsType.Departure].sortBy,
        sortDirection: state[MetricsType.Departure].sortDirection,
      },
      [MetricsType.Arrival]: {
        category: filters[FiltersSections.Arrival].category,
        values: filters[FiltersSections.Arrival].category ?
          filters[FiltersSections.Arrival][filters[FiltersSections.Arrival].category]?.map(({ value }) => value) || [] :
          [],
        currentPage: state[MetricsType.Arrival].currentPage,
        countPerPage: state[MetricsType.Arrival].countPerPage,
        sortBy: state[MetricsType.Arrival].sortBy,
        sortDirection: state[MetricsType.Arrival].sortDirection,
      },
      [MetricsType.Combo]: {
        category: filters[FiltersSections.Combo].category,
        values: filters[FiltersSections.Combo].values?.map(([from, to]) => [from.value, to.value]) || [],
        currentPage: state[MetricsType.Combo].currentPage,
        countPerPage: state[MetricsType.Combo].countPerPage,
        sortBy: state[MetricsType.Combo].sortBy,
        sortDirection: state[MetricsType.Combo].sortDirection,
      },
    };

    if (!loadSilently) {
      dispatch({
        type: "dataBeganDownloading",
      });
    }

    try {

      const response = await ai.auth(token).post(`/api/metrics/locations`, formatedFilters);

      if (!response?.data?.success) {
        toast.error(`Error getting top locations metrics`);
      }

      dispatch({
        type: "dataDownloaded",
        data: response.data.data,
      });
    } catch (error) {
      console.error(error);
      toast.error(`Error getting top locations metrics`);
    } finally {
      dispatch({
        type: "dataDownloadFinished",
      });
    }
  }, [getAccessTokenSilently, loginWithRedirect, state]);

  const fetchData = useCallback(async ({ filters = state.filters }, loadSilently = false) => {
    await getLocationsMetrics({ filters }, loadSilently);
  }, [state.filters, getLocationsMetrics]);

  const applyFilters = async ({ filters }) => {
    dispatch({
      type: "filtersChanged",
      filters,
    });

    fetchData({ filters });
  };

  return (
    <Fragment>
      <PageHeaderRowSearch h1="Top Locations"/>

      <MetricsLocationsFilters applyFilters={applyFilters}/>

      <Grid container spacing={2}>
        {[MetricsType.Departure, MetricsType.Arrival, MetricsType.Combo].map(type =>
          <Grid item sm={12} lg={6}>
            <MetricsLocationsTable
              type={type}
              isLoading={state.isLoading}
              category={state[type].category}
              data={state[type].data}
              pagination={{
                countPerPage: state[type].countPerPage,
                currentPage: state[type].currentPage,
                totalLength: state[type].totalLength,
              }}
              sorting={{
                sortBy: state[type].sortBy,
                sortDirection: state[type].sortDirection,
              }}
              fetchData={fetchData}
              parentDispatch={dispatch}
            />
          </Grid>,
        )}
      </Grid>
    </Fragment>
  );
};

export default MetricsLocations;
